import axios from "../backendAxios";
import { Season, type CompanyLocation, type Job } from "@mysnowscape/snow-lib";
import type {
  CompanyServiceOption,
  CompanyServicesBySeason,
  RouteBuilderDraft,
  RouteBuilderProperty,
  RouteBuilderSession,
} from "../../../types/routeBuilder";
import {
  checkedServiceKeys,
  serviceTypeLabel,
} from "../../../pages/Admin/Routes/RouteBuilder/lib/serviceHelpers";
import { initialPropertyServiceTime } from "../../../pages/Admin/Routes/RouteBuilder/lib/generationProperties";
import { toLatLngLiteral } from "../../../utils/geo";
import {
  draftDocumentToSession,
  draftDocumentToUiDraft,
  fromEditableRoutes,
  fromGeneratePlanResponse,
  mergeDrivingPathResponse,
  sessionToDraftData,
  toApplyPlanRequest,
  toDrivingPathRequest,
  toGeneratePlanRequest,
} from "./adapters";
import axiosRoutes from "../routes";
import type {
  ResponseDrivingPathPayload,
  ResponseGeneratePlanPayload,
} from "@mysnowscape/snow-lib";
import type {
  ApplyPlanParams,
  DrivingPathParams,
  GeneratePlanParams,
  EditableApiRoute,
  RouteBuilderDraftDocument,
} from "./types";

const ROUTE_BUILDER_DRAFT_TYPE = "route-builder" as const;

/** Slim route-builder projection (+ optional season details from Job). */
type RouteBuilderJobProjection = Pick<
  Job,
  | "name"
  | "address"
  | "landscape"
  | "snow"
  | "location"
  | "companyId"
  | "instructions"
> & {
  _id: string;
  estimatedServiceTimeSeconds?: number;
  routes?: Array<{ _id?: string; id?: string; name?: string; color?: string }>;
  updatedAt?: string;
};

function mapJobToProperty(
  job: RouteBuilderJobProjection,
  season: Season,
  companyId: string,
): RouteBuilderProperty {
  const seasonDetails = season === Season.landscape ? job.landscape : job.snow;
  const notes = seasonDetails?.instructions ?? job.instructions ?? "";
  const serviceKeys = checkedServiceKeys(seasonDetails);
  const serviceKey = serviceKeys[0];
  const assignedRoutes = (job.routes ?? []).flatMap((route) => {
    const id = String(route._id ?? route.id ?? "");
    const name = route.name?.trim();
    if (!id || !name) return [];
    return [{ id, name, color: route.color }];
  });

  return {
    jobId: String(job._id),
    companyId: String(job.companyId ?? companyId),
    name: job.name,
    address: job.address ?? {},
    location: toLatLngLiteral(job.location) ?? { lat: 0, lng: 0 },
    propertyType: serviceKey ?? "residential",
    serviceType: serviceKey ?? "residential",
    ...(serviceKeys.length > 0 ? { serviceTypes: serviceKeys } : {}),
    zoneId: "unknown",
    zoneName: "Unknown",
    season,
    notes,
    originalNotes: notes,
    ...initialPropertyServiceTime(job, season),
    lock: null,
    ...(assignedRoutes.length > 0 ? { assignedRoutes } : {}),
    ...(job.updatedAt ? { updatedAt: job.updatedAt } : {}),
  };
}

function normalizeCompanyLocation(location: CompanyLocation): CompanyLocation {
  const coords = toLatLngLiteral(location.location);
  return coords ? { ...location, location: coords } : location;
}

export function unwrapLocationsPayload(data: unknown): CompanyLocation[] {
  if (Array.isArray(data)) {
    return (data as CompanyLocation[]).map(normalizeCompanyLocation);
  }
  if (
    data &&
    typeof data === "object" &&
    Array.isArray((data as { locations?: unknown }).locations)
  ) {
    return (data as { locations: CompanyLocation[] }).locations.map(
      normalizeCompanyLocation,
    );
  }
  return [];
}

function normalizeServiceOptions(list: unknown): CompanyServiceOption[] {
  if (!Array.isArray(list) || list.length === 0) return [];
  return list
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const row = item as { key?: string; label?: string };
      if (!row.key) return null;
      return {
        key: row.key,
        label: row.label || serviceTypeLabel(row.key),
      };
    })
    .filter((option): option is CompanyServiceOption => Boolean(option));
}

/** Route Builder HTTP facade (generate-plan, driving-path, apply-plan, drafts, wizard data). */
const routeBuilder = {
  async loadRoutesForEdit(
    routeIds: string[],
    companyId: string,
  ): Promise<RouteBuilderSession> {
    const routes = await Promise.all(
      routeIds.map((routeId) => axiosRoutes.getRouteWithJobs(routeId)),
    );
    return fromEditableRoutes(routes as EditableApiRoute[], companyId);
  },

  async generatePlan(params: GeneratePlanParams): Promise<RouteBuilderSession> {
    const request = toGeneratePlanRequest(params);
    const response = await axios.post<ResponseGeneratePlanPayload>(
      "route/builder/generate-plan",
      request,
    );
    return fromGeneratePlanResponse(response.data, params);
  },

  async drivingPath(params: DrivingPathParams): Promise<RouteBuilderSession> {
    const request = toDrivingPathRequest(params);
    const response = await axios.post<ResponseDrivingPathPayload>(
      "route/builder/driving-path",
      request,
    );
    return mergeDrivingPathResponse(
      params.session,
      response.data,
      params.routeIds,
    );
  },

  async applyPlan(
    params: ApplyPlanParams,
    onSessionUpdated: (session: RouteBuilderSession) => void,
  ): Promise<void> {
    const request = toApplyPlanRequest(params);
    let session = params.session;
    const season = session.inputs.season;
    for (const property of session.availableProperties) {
      const notes = property.notes ?? "";
      if (notes === (property.originalNotes ?? "")) continue;
      const { data: job } = await axios.get<Job>(`job/${property.jobId}`);
      if ((job[season]?.instructions ?? job.instructions ?? "") !== notes) {
        if (String(job.companyId) !== params.companyId) {
          throw new Error(
            "Only the property owner can edit crew instructions.",
          );
        }
        await axios.post(
          "job/update",
          { ...job, [season]: { ...job[season], instructions: notes } },
          { params: { currentCompanyId: params.companyId } },
        );
      }
      session = {
        ...session,
        availableProperties: session.availableProperties.map((candidate) =>
          candidate.jobId === property.jobId
            ? { ...candidate, originalNotes: notes }
            : candidate,
        ),
      };
      onSessionUpdated(session);
    }
    if (session.builderMode === "edit") {
      for (let index = 0; index < request.routes.length; index += 1) {
        const route = request.routes[index];
        const response = await axios.post<{ route: EditableApiRoute }>(
          route._id ? "route/update" : "route/add",
          route,
        );
        if (route._id) continue;
        const saved = response.data.route;
        if (!saved?._id) {
          throw new Error("The created route response is missing its ID.");
        }
        session = {
          ...session,
          routePersistence: {
            ...session.routePersistence,
            [session.routes[index].id]: {
              _id: saved._id,
              tags: saved.tags ?? [],
              isActive: saved.isActive !== false,
              expiresAt: saved.expiresAt ?? route.expiresAt,
            },
          },
        };
        onSessionUpdated(session);
      }
      return;
    }
    await axios.post("route/builder/apply-plan", request);
  },

  async listDrafts(companyId: string): Promise<RouteBuilderDraft[]> {
    const response = await axios.get<RouteBuilderDraftDocument[]>("drafts", {
      params: { companyId, type: ROUTE_BUILDER_DRAFT_TYPE },
    });
    return response.data.map(draftDocumentToUiDraft);
  },

  async getDraft(draftId: string): Promise<RouteBuilderSession | null> {
    try {
      const response = await axios.get<RouteBuilderDraftDocument>(
        `drafts/${draftId}`,
      );
      return draftDocumentToSession(response.data);
    } catch {
      return null;
    }
  },

  async putDraft(args: {
    draftId: string;
    companyId: string;
    name: string;
    session: RouteBuilderSession;
  }): Promise<RouteBuilderDraft> {
    const doc: RouteBuilderDraftDocument = {
      _id: args.draftId,
      type: ROUTE_BUILDER_DRAFT_TYPE,
      companyId: args.companyId,
      title: args.name,
      data: sessionToDraftData(args.session),
    };
    await axios.put("drafts", doc);
    return draftDocumentToUiDraft(doc);
  },

  async deleteDraft(draftId: string): Promise<void> {
    await axios.delete(`drafts/${draftId}`);
  },

  async loadBuilderProperties(args: {
    companyId: string;
    season: Season;
    services?: string[];
    servicedAfter?: string;
    search?: string;
    take?: number;
    signal?: AbortSignal;
    /** Skip the slim projection so address + assigned routes match the route editor. */
    includeAssignedRoutes?: boolean;
  }): Promise<RouteBuilderProperty[]> {
    const params: Record<string, string> = {
      "filter[season]": args.season,
      "filter[hasLocation]": "1",
    };
    if (!args.includeAssignedRoutes) {
      params.projectionName = "route-builder";
    }
    if (args.services?.length) {
      params["filter[services]"] = args.services.join(",");
    }
    if (args.servicedAfter) {
      params["filter[servicedAfter]"] = args.servicedAfter;
    }
    if (args.search?.trim()) {
      params.search = args.search.trim();
    }
    params.take = String(args.take ?? 10000);

    const response = await axios.get(`job/company/${args.companyId}`, {
      params,
      signal: args.signal,
    });
    const jobs = (response.data?.jobs ??
      response.data ??
      []) as RouteBuilderJobProjection[];
    const list = Array.isArray(jobs) ? jobs : [];
    return list.map((job) =>
      mapJobToProperty(job, args.season, args.companyId),
    );
  },

  async loadBuilderLocations(args: {
    companyId: string;
  }): Promise<CompanyLocation[]> {
    const response = await axios.get(`company/${args.companyId}/locations`);
    return unwrapLocationsPayload(response.data);
  },

  async loadBuilderServices(args: {
    companyId: string;
  }): Promise<CompanyServicesBySeason> {
    const response = await axios.get(`company/${args.companyId}/services`);
    const data = response.data ?? {};
    return {
      snow: normalizeServiceOptions(data.snow),
      landscape: normalizeServiceOptions(data.landscape),
    };
  },
};

export default routeBuilder;
